import { supabase } from './supabase';

/**
 * Branch Detection from Subdomain
 *
 * Maps subdomains to branches automatically.
 *
 * Examples:
 *   london.helpyateem.org    → London branch
 *   manchester.helpyateem.org → Manchester branch
 *   donate.helpyateem.org    → Main/default branch
 *   localhost:3000           → Development (uses env or fetches first branch)
 */

// Cache for branch data to avoid repeated API calls
let cachedBranch = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Extract subdomain from hostname
 * @returns {string|null} subdomain or null if none
 */
export const getSubdomain = () => {
  const hostname = window.location.hostname;

  // Handle localhost (development)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return process.env.REACT_APP_DEV_SUBDOMAIN || null;
  }

  // Split hostname into parts
  const parts = hostname.split('.');

  // If we have more than 2 parts (e.g., london.helpyateem.org)
  // First part is the subdomain
  if (parts.length > 2) {
    const subdomain = parts[0].toLowerCase();

    // Ignore common non-branch subdomains
    const ignoredSubdomains = ['www', 'donate', 'app', 'api', 'admin'];
    if (ignoredSubdomains.includes(subdomain)) {
      return null;
    }

    return subdomain;
  }

  return null;
};

/**
 * Fetch branch by subdomain/slug from database
 * @param {string} subdomain
 * @param {string} organizationId
 * @returns {Promise<{branch: object|null, error: string|null}>}
 */
export const fetchBranchBySubdomain = async (subdomain, organizationId) => {
  try {
    // First try to find branch by slug/subdomain field
    let { data: branch, error } = await supabase
      .from('branches')
      .select('*')
      .eq('organisation_id', organizationId)
      .ilike('name', `%${subdomain}%`) // Match subdomain in branch name
      .single();

    // If not found by name, try slug field if it exists
    if (error || !branch) {
      const { data: branchBySlug, error: slugError } = await supabase
        .from('branches')
        .select('*')
        .eq('organisation_id', organizationId)
        .eq('slug', subdomain)
        .single();

      if (!slugError && branchBySlug) {
        branch = branchBySlug;
        error = null;
      }
    }

    if (error || !branch) {
      return { branch: null, error: 'Branch not found for subdomain: ' + subdomain };
    }

    return { branch, error: null };
  } catch (err) {
    console.error('Error fetching branch:', err);
    return { branch: null, error: err.message };
  }
};

/**
 * Fetch the main/default branch for an organization
 * @param {string} organizationId
 * @returns {Promise<{branch: object|null, error: string|null}>}
 */
export const fetchMainBranch = async (organizationId) => {
  try {
    // Try to find main branch first
    let { data: branch, error } = await supabase
      .from('branches')
      .select('*')
      .eq('organisation_id', organizationId)
      .eq('is_main', true)
      .single();

    // If no main branch, get the first one
    if (error || !branch) {
      const { data: firstBranch, error: firstError } = await supabase
        .from('branches')
        .select('*')
        .eq('organisation_id', organizationId)
        .order('created_at', { ascending: true })
        .limit(1)
        .single();

      if (!firstError && firstBranch) {
        branch = firstBranch;
        error = null;
      }
    }

    if (error || !branch) {
      return { branch: null, error: 'No branch found for organization' };
    }

    return { branch, error: null };
  } catch (err) {
    console.error('Error fetching main branch:', err);
    return { branch: null, error: err.message };
  }
};

/**
 * Get current branch based on subdomain
 * Uses caching to avoid repeated API calls
 * @param {string} organizationId
 * @returns {Promise<{branch: object|null, error: string|null}>}
 */
export const getCurrentBranch = async (organizationId) => {
  // Check cache first
  if (cachedBranch && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_DURATION)) {
    return { branch: cachedBranch, error: null };
  }

  const subdomain = getSubdomain();
  let result;
  if (subdomain) {
    // Subdomain exists - fetch specific branch
    result = await fetchBranchBySubdomain(subdomain, organizationId);

    // If branch not found for subdomain, fall back to main branch
    if (result.error || !result.branch) {
      console.warn(`Branch not found for subdomain "${subdomain}", using main branch`);
      result = await fetchMainBranch(organizationId);
    }
  } else {
    // No subdomain - use main/default branch
    result = await fetchMainBranch(organizationId);
  }

  // Cache the result
  if (result.branch) {
    cachedBranch = result.branch;
    cacheTimestamp = Date.now();
  }

  return result;
};

/**
 * Clear the branch cache (useful when switching branches in dev)
 */
export const clearBranchCache = () => {
  cachedBranch = null;
  cacheTimestamp = null;
};

/**
 * Get branch ID for current subdomain
 * Convenience function that just returns the ID
 * @param {string} organizationId
 * @returns {Promise<string|null>}
 */
export const getCurrentBranchId = async (organizationId) => {
  const { branch } = await getCurrentBranch(organizationId);
  return branch?.id || null;
};
